/* sugar:assets/card-grid.js@2.0.0 */
/**
 * Card grid — the grid/slider engine behind Blog List, Collections List and
 * Featured Collection.
 *
 * One copy of this file serves every card grid on the page. The markup carries data
 * attributes only, so nothing here depends on a section's class names:
 *
 *   [data-card-grid]        the wrapper around one grid, carrying its whole config
 *     data-splide-id        id of the .splide element inside it
 *     data-root             class of the section root, for the grid-mode flag
 *     data-item-class       class Shopify puts on each card's block wrapper
 *     data-desktop-layout   grid | slider     data-mobile-layout   grid | slider
 *     data-desktop-per-page / data-mobile-per-page
 *     data-desktop-gap / data-mobile-gap
 *     data-desktop-card-width / data-mobile-card-width   a fixed card width (0 = per page)
 *     data-motion           stop | loop | loop_center (loop with the current card centred)
 *
 * A viewport set to grid never mounts Splide at all: the wrapper takes .is-grid-mode
 * and the CSS lays the cards out as a plain grid. A viewport set to slider is drawn as
 * its row before Splide arrives (snippet-card-shell.css), so the mount changes nothing
 * on screen; if Splide fails to load, that viewport falls back to the grid.
 */

(function () {
  'use strict';

  if (window.SugarCardGrid) return;

  var RETRY_MS = 120;
  var MOBILE = '(max-width: 749px)';

  function num(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /**
   * Splide leaves inline sizes behind when a slider is torn down. Clear them so the
   * same markup can lay out as a grid.
   */
  function clearSplideStyles(splideEl) {
    splideEl.classList.remove('is-initialized', 'is-active', 'is-overflow', 'is-rendered');
    splideEl.style.visibility = '';

    var track = splideEl.querySelector('.splide__track');
    if (track) {
      track.style.transform = '';
      track.style.paddingLeft = '';
      track.style.paddingRight = '';
      track.style.height = '';
    }

    var list = splideEl.querySelector('.splide__list');
    if (!list) return;
    list.style.transform = '';
    list.style.transition = '';
    list.style.width = '';
    list.style.height = '';

    Array.prototype.forEach.call(list.children, function (child) {
      if (!child || !child.style) return;
      child.style.width = '';
      child.style.marginRight = '';
      child.style.marginLeft = '';
      child.style.transform = '';
    });
  }

  /**
   * Splide takes the track list's *direct children* as its slides, and Shopify wraps
   * every theme block in a div of its own — so the wrapper is the slide, not the card
   * markup inside it. Mark the wrappers, and lift anything that is not a card (Card
   * Frame, Section Animation) out of the track before mounting, or it becomes a blank
   * slide.
   */
  function prepareSlides(splideEl, itemClass) {
    var list = splideEl.querySelector('.splide__list');
    if (!list || !itemClass) return;

    Array.prototype.slice.call(list.children).forEach(function (child) {
      if (child.classList.contains(itemClass)) {
        child.classList.add('splide__slide');
      } else if (splideEl.parentNode) {
        splideEl.parentNode.insertBefore(child, splideEl);
      }
    });
  }

  function destroy(splideEl) {
    if (splideEl._scChrome) {
      try {
        splideEl._scChrome.detach();
      } catch (e) {}
      splideEl._scChrome = null;
    }
    if (splideEl._splideInstance) {
      try {
        splideEl._splideInstance.destroy(true);
      } catch (e) {}
      splideEl._splideInstance = null;
    }
  }

  /** Returns false when Splide was wanted but is not available yet, so the caller retries. */
  function mount(wrapper) {
    var splideEl = wrapper.querySelector('.splide');
    if (!splideEl) return true;

    var root = wrapper.dataset.root ? wrapper.closest(wrapper.dataset.root) : null;

    function setGridMode(on) {
      wrapper.classList.toggle('is-grid-mode', on);
      if (root) root.classList.toggle('is-card-grid-mode', on);
    }

    destroy(splideEl);
    prepareSlides(splideEl, wrapper.dataset.itemClass);

    var onMobile = window.matchMedia(MOBILE).matches;
    var layout = onMobile ? wrapper.dataset.mobileLayout : wrapper.dataset.desktopLayout;

    if (layout !== 'slider') {
      clearSplideStyles(splideEl);
      setGridMode(true);
      return true;
    }

    // While Splide is on its way the stylesheet already draws the row it will mount into,
    // so leave that alone. Only a load that finished without it falls back to the grid, so
    // the cards stay reachable.
    if (typeof Splide === 'undefined') {
      if (document.documentElement.getAttribute('data-splide-runtime-loaded') !== 'true') {
        if (wrapper.classList.contains('is-grid-mode')) setGridMode(false);
        return false;
      }
      clearSplideStyles(splideEl);
      setGridMode(true);
      return true;
    }

    setGridMode(false);

    var instance;
    var desktopWidth = num(wrapper.dataset.desktopCardWidth, 0);
    var mobileWidth = num(wrapper.dataset.mobileCardWidth, 0);
    var motion = wrapper.dataset.motion || 'stop';
    try {
      instance = new Splide(splideEl, {
        type: motion === 'loop' || motion === 'loop_center' ? 'loop' : 'slide',
        fixedWidth: desktopWidth || undefined,
        perPage: desktopWidth ? 1 : num(wrapper.dataset.desktopPerPage, 3),
        focus: motion === 'loop_center' ? 'center' : undefined,
        perMove: 1,
        gap: num(wrapper.dataset.desktopGap, 16) + 'px',
        arrows: false,
        pagination: false,
        breakpoints: {
          749: {
            perPage: mobileWidth ? 1 : num(wrapper.dataset.mobilePerPage, 1),
            fixedWidth: mobileWidth || undefined,
            gap: num(wrapper.dataset.mobileGap, 16) + 'px'
          }
        }
      });
    } catch (e) {
      clearSplideStyles(splideEl);
      setGridMode(true);
      return false;
    }

    splideEl._splideInstance = instance;
    instance.mount();

    if (window.SugarSliderChrome && root) {
      splideEl._scChrome = window.SugarSliderChrome.attach(instance, root, { dotMode: 'page' });
    }

    return true;
  }

  var pending = false;

  function init(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var grids = host.querySelectorAll('[data-card-grid]');
    var ready = true;

    Array.prototype.forEach.call(grids, function (wrapper) {
      if (!mount(wrapper)) ready = false;
    });

    if (!ready && !pending) {
      pending = true;
      setTimeout(function () {
        pending = false;
        init(scope);
      }, RETRY_MS);
    }
  }

  window.SugarCardGrid = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // The viewport can cross the breakpoint between grid and slider.
  var mq = window.matchMedia(MOBILE);
  var onChange = function () {
    init(document);
  };
  if (mq.addEventListener) {
    mq.addEventListener('change', onChange);
  } else if (mq.addListener) {
    mq.addListener(onChange);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:block:select', function (event) {
      init(event.target.closest('.shopify-section') || document);
    });
  }
})();
